import React, { useMemo } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { OptionKey, Question } from '../data/types';

interface QuizSidebarProps {
  day: number;
  totalCount: number;
  answeredCount: number;
  remainingCount: number;
  flaggedCount: number;
  earnedScore?: number;
  maxScore?: number;
  questions: Question[];
  answers: Record<number, OptionKey>;
  flagged: number[];
  currentIndex: number;
  isCollapsed: boolean;
  canSubmit: boolean;
  lockedQuestionIds?: number[];
  isDayCompleted?: boolean;
  isSaving?: boolean;
  onToggleCollapse: () => void;
  onSelectQuestion: (index: number) => void;
  onOpenSubmitModal: () => void;
}

export const QuizSidebar: React.FC<QuizSidebarProps> = ({
  day,
  totalCount,
  answeredCount,
  remainingCount,
  flaggedCount,
  earnedScore,
  maxScore,
  questions,
  answers,
  flagged,
  currentIndex,
  isCollapsed,
  canSubmit,
  lockedQuestionIds = [],
  isDayCompleted = false,
  isSaving = false,
  onToggleCollapse,
  onSelectQuestion,
  onOpenSubmitModal,
}) => {
  const completedCount = useMemo(() => {
    return questions.filter((q) => Boolean(answers[q.id]) || lockedQuestionIds.includes(q.id)).length;
  }, [questions, answers, lockedQuestionIds]);
  if (isCollapsed) {
    return (
      <aside className="fc-sidebar-rail">
        <button
          type="button"
          className="fc-rail-btn"
          onClick={onToggleCollapse}
          title="Mở danh sách câu hỏi"
        >
          <ChevronRight size={16} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="fc-sidebar">
      <div className="fc-sidebar-header">
        <div>
          <div className="fc-sidebar-day-label">Ngày {day}</div>
          <div className="fc-sidebar-sub">{totalCount} câu hỏi</div>
        </div>
        <button
          type="button"
          className="fc-collapse-btn"
          onClick={onToggleCollapse}
          title="Thu gọn"
        >
          <ChevronLeft size={16} />
        </button>
      </div>

      <div className="fc-grid-container">
        {questions.map((q, idx) => {
          const hasAnswer = Boolean(answers[q.id]);
          const isQuestionLocked = lockedQuestionIds.includes(q.id);
          const isCompleted = isDayCompleted ? hasAnswer : isQuestionLocked;
          const isAnswered = !isCompleted && hasAnswer;
          const isCurrent = idx === currentIndex;
          const isItemFlagged = flagged.includes(q.id);

          return (
            <button
              key={q.id}
              type="button"
              className={`fc-grid-item ${isCompleted ? 'completed' : isAnswered ? 'answered' : ''} ${isCurrent ? 'current' : ''}`}
              onClick={() => {
                if (!isSaving) onSelectQuestion(idx);
              }}
              disabled={isSaving}
              title={`Câu ${idx + 1}: ${q.competency}${
                isCompleted
                  ? ' (Đã hoàn thành)'
                  : isAnswered
                  ? ' (Đã chọn)'
                  : ' (Chưa trả lời)'
              }`}
            >
              <span>{idx + 1}</span>
              {isCompleted && (
                <span className="fc-completed-badge">
                  <Check size={9} strokeWidth={3} />
                </span>
              )}
              {isItemFlagged && <span className="fc-flag-dot" />}
            </button>
          );
        })}
      </div>

      <div className="fc-legend">
        <div className="fc-legend-item">
          <span className="fc-legend-box comp" />
          <span>Đã hoàn thành</span>
        </div>
        <div className="fc-legend-item">
          <span className="fc-legend-box cur" />
          <span>Câu hiện tại</span>
        </div>
        <div className="fc-legend-item">
          <span className="fc-legend-box unans" />
          <span>Chưa trả lời</span>
        </div>
        <div className="fc-legend-item">
          <span className="fc-legend-box flg" />
          <span>Đánh dấu</span>
        </div>
      </div>

      <div className="fc-stats-panel">
        <div className="fc-stat-col">
          <div className="fc-stat-num">{completedCount}</div>
          <div className="fc-stat-lbl">Hoàn thành</div>
        </div>
        <div className="fc-stat-col">
          <div className="fc-stat-num">{remainingCount}</div>
          <div className="fc-stat-lbl">Còn lại</div>
        </div>
        <div className="fc-stat-col">
          <div className="fc-stat-num">{flaggedCount}</div>
          <div className="fc-stat-lbl">Đánh dấu</div>
        </div>
      </div>

      {earnedScore !== undefined && maxScore !== undefined && (
        <div className="fc-score-row">
          <div className="fc-score-box earned">
            <span className="fc-score-label">Tổng điểm</span>
            <span className="fc-score-value">{earnedScore}</span>
          </div>
          <div className="fc-score-divider" />
          <div className="fc-score-box max">
            <span className="fc-score-label">Tối đa</span>
            <span className="fc-score-value">{maxScore}</span>
          </div>
        </div>
      )}

      {canSubmit && (
        <button type="button" className="fc-submit-btn" onClick={onOpenSubmitModal}>
          Hoàn thành Ngày {day}
        </button>
      )}
    </aside>
  );
};
